import { get, getCtx, set } from '@dimjs/utils';
import { utils } from '../../utils/index.js';
import { type WebviewJavascriptBridgeApi } from '../type.api.js';
import {
  type CallNtvData,
  type FabricCallbackData,
  type PlainObjectNoFunction,
} from '../type.builtin.js';
import {
  isFullyInitialized,
  ntvInit,
  webCallbackToNtv,
} from './builtin-ntv.js';
import {
  type IOSMessageFromNativeCall,
  type IOSSendWebCallbackDataToNtv,
} from './types.ios.js';
const { log, parse } = utils;
/**
 * 保存Web端注册的方法结构, 方便native可以按需调用
 */
const registeredCallbacks = {
  /**
   * 保存同步注册的方法.
   */
  sync: {},
  /**
   * 保存异步注册的方法
   */
  async: {},
};
/**
 * Web端已经准备就绪, 如果Native有队列消息提前调用了客户端, 这个时候就可以处理已存储的队列消息.
 * Android/ios实现不同;
 * iOS是将消息pending 到native直到web通知native准备就绪, 才执行.
 * Android 则是直接等bridge-js文件加载完毕直接发送消息到web, web端需要自己存储并合适时机去处理.
 */
export const webIsReady = () => {
  ntvInit();
};
/**
 * 注册Javasript API给Native端调用, 同名方法多次注册, 将会覆盖, 默认为异步注册.
 * 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @example
 * ```ts
 * register('a', function () {})
 * register('a.b', function () {})
 * register('a', { c: function () {} })
 * register('a.b', { c: function () {} })
 * ```
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体
 * @param sync 是否为同步注册, 默认 `false`
 */
function register(
  method: string,
  fun: Function | PlainObjectNoFunction,
  type: 'sync' | 'async' = 'async'
) {
  const queue =
    type === 'sync' ? registeredCallbacks.sync : registeredCallbacks.async;
  const exist = queue[method] || get(queue, method);
  if (exist) {
    log.logWarn(`方法(${method})已经被注册, 使用新方法覆盖老方法!`);
  }
  set(queue, method, fun);
}
/**
 * 注册一个异步方法, 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体, 函数, 或者对象(里面包含多个函数)
 */
export const registerAsync: WebviewJavascriptBridgeApi['registerAsync'] = (
  name,
  fun
) => {
  register(name, fun, 'async');
};
/**
 * 注册一个同步方法, 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体, 函数, 或者对象(里面包含多个函数)
 */
export const registerSync: WebviewJavascriptBridgeApi['registerSync'] = (
  name,
  fun
) => {
  register(name, fun, 'sync');
};
/**
 * Native 检查javascript注册的回调里面是否存在此命名空间的方法
 * @param method 方法名, 命名空间名
 */
export const hasJavascriptMethod = (method: string): boolean => {
  const { async, sync } = registeredCallbacks;
  log.logData('hasJavascriptMethod', method, async, sync);
  return !!(
    sync[method] ||
    get(sync, method, false) ||
    async[method] ||
    get(async, method, false)
  );
};

/**
 * Native主动调用Web端的消息通道. Note此处需要注意IOS如果是json.stringify(“字符串“) 他是不会进行二次序列化处理.
 * IOS认为字符串不是一个需要序列化的字典或者数组.
 * 当Native 主动调用H5序列化的时候如果是字符串是不会进行序列化处理的.
 * @param message Native发送到Web的消息体
 */
export const handleMessageFromNative = (message: IOSMessageFromNativeCall) => {
  const { callbackId, data, method } = message;
  const ntvCallArgs: unknown = parse.jsonParse(data || '{}');
  const callbackToNtvData: IOSSendWebCallbackDataToNtv = {
    id: callbackId,
    complete: true,
    data: {
      code: '0',
      data: null,
    },
  };
  const { async, sync } = registeredCallbacks;
  const syncFn = sync[method] || get(sync, method);
  const asyncFn = async[method] || get(async, method);

  if (syncFn) {
    log.logData(
      `[iOS] 接收Ntv主动调用Web, 方法(${method})`,
      `isInitialized:`,
      isFullyInitialized(),
      '消息体:',
      message,
      'WEB同步方法:',
      syncFn
    );
    try {
      const syncCtx = sync[method] ? sync : getCtx(sync, method);
      callbackToNtvData.data = syncFn.apply(syncCtx, [ntvCallArgs]);
      webCallbackToNtv(callbackToNtvData);
    } catch (err) {
      log.logError(
        '[iOS] handleMessageFromNative() `syncFn.apply()` threw',
        err
      );
    }
  } else if (asyncFn) {
    log.logData(
      `[iOS] 接收Ntv主动调用Web, 方法(${method})`,
      `isInitialized:`,
      isFullyInitialized(),
      '消息体:',
      message,
      'WEB异步方法:',
      asyncFn
    );
    try {
      const asyncCtx = async[method] ? async : getCtx(async, method);
      asyncFn.apply(asyncCtx, [
        ntvCallArgs,
        (data: FabricCallbackData<CallNtvData>, complete?: boolean) => {
          callbackToNtvData.data = data;
          callbackToNtvData.complete = complete !== false;
          webCallbackToNtv(callbackToNtvData);
        },
      ]);
    } catch (err) {
      log.logError(
        '[iOS] handleMessageFromNative() `asyncFn.apply()` throw',
        err
      );
    }
  } else {
    log.logError(
      '[iOS] handleMessageFromNative()',
      `No Registered Javascript Method (${method}) Found In Web!`
    );
  }
};
